import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'
import { Article } from '../../types'
import { articleUrl, useArticleCategories } from './articleData'

type ArticleRow = Pick<Article, 'id' | 'slug' | 'title' | 'category' | 'extra_categories' | 'published' | 'updated_at'>

export default function ArticlesList() {
  const navigate = useNavigate()
  const { isAdmin } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')

  const articlesQuery = useQuery({
    queryKey: ['articles'],
    enabled: isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('id, slug, title, category, extra_categories, published, updated_at')
        .order('updated_at', { ascending: false })

      if (error) throw error
      return (data || []) as ArticleRow[]
    },
  })
  const categoriesQuery = useArticleCategories(isAdmin)

  const categoryNames = useMemo(() => {
    const map = new Map<string, string>()
    ;(categoriesQuery.data || []).forEach((c) => map.set(c.slug, c.name))
    return map
  }, [categoriesQuery.data])

  const filteredArticles = useMemo(() => {
    let filtered = articlesQuery.data || []

    if (categoryFilter === 'none') {
      filtered = filtered.filter((a) => !a.category)
    } else if (categoryFilter !== 'all') {
      filtered = filtered.filter(
        (a) => a.category === categoryFilter || a.extra_categories?.includes(categoryFilter)
      )
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter((a) => a.title.toLowerCase().includes(search))
    }

    return filtered
  }, [articlesQuery.data, categoryFilter, searchTerm])

  if (!isAdmin) return null

  if (articlesQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  const error = articlesQuery.error || categoriesQuery.error

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Artikler ({filteredArticles.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/articles/new')}
        >
          Ny artikel
        </Button>
      </Box>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Søg på titel..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 240 }}>
            <InputLabel>Kategori</InputLabel>
            <Select
              value={categoryFilter}
              label="Kategori"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              {(categoriesQuery.data || []).map((c) => (
                <MenuItem key={c.slug} value={c.slug}>
                  {c.name}
                </MenuItem>
              ))}
              <MenuItem value="none">Uden kategori</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      {filteredArticles.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Ingen artikler fundet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchTerm || categoryFilter !== 'all' ? 'Prøv en anden søgning' : 'Opret den første artikel med "Ny artikel"'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Titel</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Kategori</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Udgivet</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Opdateret</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 240 }} align="right">Handling</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredArticles.map((article) => (
                <TableRow key={article.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {article.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      /{article.slug}/
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {article.category ? (
                      <Typography variant="body2">
                        {categoryNames.get(article.category) || article.category}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                    {article.extra_categories?.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Også: {article.extra_categories.map((c) => categoryNames.get(c) || c).join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={article.published ? 'Ja' : 'Nej'}
                      size="small"
                      color={article.published ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {new Date(article.updated_at).toLocaleDateString('da-DK', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {article.published && (
                      <Button
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        href={articleUrl(article.slug)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mr: 1 }}
                      >
                        Se på siden
                      </Button>
                    )}
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/articles/edit/${article.id}`)}
                    >
                      Rediger
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
